import create from 'zustand';
import type { Toast, ToastType } from '../../types';

interface ToastStore {
  toasts: Toast[];
  addToast: (message: string, type?: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

/**
 * Toast notification store
 * Manages the state of all toast notifications
 */
export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  addToast: (message, type = 'info', duration = 5000) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const toast: Toast = {
      id,
      message,
      type,
      duration,
    };

    set((state) => ({
      toasts: [...state.toasts, toast],
    }));
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },
}));

/**
 * Helper functions for showing toasts
 * Usage:
 * ```ts
 * import { showSuccess, showError } from '@/zustandstore/toast';
 * showSuccess('Operation completed!');
 * showError('Something went wrong');
 * ```
 */
export const showToast = (message: string, type?: ToastType, duration?: number) => {
  useToastStore.getState().addToast(message, type, duration);
};

export const showSuccess = (message: string, duration?: number) => {
  showToast(message, 'success', duration);
};

export const showError = (message: string, duration?: number) => {
  showToast(message, 'error', duration);
};

export const showWarning = (message: string, duration?: number) => {
  showToast(message, 'warning', duration);
};

export const showInfo = (message: string, duration?: number) => {
  showToast(message, 'info', duration);
};
